import { useMemo } from 'react';
import { useNativeState } from '@shared/core/native';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import type { EventPageSlug } from '@apps/guest/shared/constants/eventPageSlugs';

const NATIVE_BRANNAN_OVERRIDE_SLUGS: ReadonlySet<string> = new Set<EventPageSlug>([EVENT_PAGE_SLUGS.Accommodations, EVENT_PAGE_SLUGS.ThingsToDo]);

/**
 * Determines whether the current page should receive native-specific Brannan layout behavior.
 *
 * Pages in the override set (accommodations, things-to-do) bypass the single-page Brannan shell
 * when rendered inside native webviews, falling back to multi-page Brannan instead.
 *
 * @param pageSlug - The current page's slug, typically from route or navigation context.
 * @returns An object containing:
 * - `isNativeBrannanOverridePage`: `true` if the app is in native mode and the page is in the override set.
 */
export const useNativeBrannanOverrideCheck = (pageSlug?: Readonly<string>) => {
  const { isNative } = useNativeState();

  return useMemo(() => {
    const isNativeBrannanOverridePage = isNative && typeof pageSlug === 'string' && NATIVE_BRANNAN_OVERRIDE_SLUGS.has(pageSlug);

    return { isNativeBrannanOverridePage };
  }, [isNative, pageSlug]);
};
